<template>
  <div
    style="position: relative; width: 100%;"
    :class="{
      dragged: dragging && isDraggingSelf,
      'question-block': true
    }"
  >
    <v-layout :class="`note-card ${dragSelected ? 'blue lighten-4' : ''}`">
      <v-flex>
        <v-row>
          <v-col v-if="viewMode == 'full'" class="shrink col pt-1 pb-0 px-0">
            <v-btn
              icon
              :disabled="dragging"
              @dragstart.stop.prevent="$emit('dragStart', $event)"
              @mousedown.stop
              @click.stop="click"
              draggable="true"
              color="grey lighten-1"
              ><v-icon v-show="hovering">drag_indicator</v-icon></v-btn
            >
          </v-col>
          <v-col
            :class="{
              shrink: true,
              'pa-1': true,
              'pl-0': viewMode == 'full',
              'pl-3': viewMode == 'grid'
            }"
          >
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  v-on="on"
                  class=""
                  :disabled="!userCanEdit"
                  :style="noteState.value == 0 ? 'opacity: 0.4;' : ''"
                >
                  <v-icon class="" :color="noteState.color">
                    {{ noteState.icon }}
                  </v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="status in noteStatuses"
                  @click="noteState = status.value"
                  :key="status.value"
                >
                  <v-list-item-icon>
                    <v-icon :color="status.color">{{ status.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ status.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
          <v-col class="pb-0 pl-0">
            <div v-if="!userCanEdit" v-text="cardTitle.html" class="ql-editor"></div>
            <content-editor
              v-else
              v-model="cardTitle"
              ref="titleEditor"
              :key="card.id"
              :placeholder="'Untitled Card'"
              :disabled="dragging"
              highjackUndo
              :preventTab="true"
              :emitText="true"
              :hijackCopy="dragSelected"
              :hijackPaste="false"
              :allowFormats="false"
              :allowMarkdown="false"
              allowTraversal
              :allowEnterMoveText="false"
              @keyEnter="$emit('addNewLine', { order: 0 })"
              @keyDownLastLine="
                traverse({ toRef: 'summaryEditor', direction: 'down', event: $event })
              "
              @keyUpFirstLine="$emit('traverseUp', { direction: 'up', event: $event })"
              @keyBackspaceEmpty="$emit('deleteCard', { card: card, delta: false })"
              @keyShiftTab="$emit('tabBack')"
              @undo="$emit('undo')"
              @redo="$emit('redo')"
              @copy="$emit('copyBlocks')"
              @focus="focused = true"
              @blur="focused = false"
              @paste="log('paste this')"
            ></content-editor>
          </v-col>
          <v-col class="py-0 shrink">
            <WorkspaceUserSelect
              v-if="userCanEdit"
              v-model="assignedUser"
              :typeAheadList="workspaceMembers"
              label=""
              :dropDownList="workspaceMembers"
              :hideDetails="true"
              :hideIcon="true"
              :avatarOnly="true"
              :removeable="false"
              :solo="true"
              color="transparent"
              style="min-width: 56px;"
            ></WorkspaceUserSelect>
          </v-col>
        </v-row>
        <v-row align="end">
          <v-col
            :class="{
              'card-summary': true,
              'pb-0': true,
              'pl-10': viewMode == 'full',
              'pl-5': viewMode == 'grid'
            }"
          >
            <div v-if="!userCanEdit" v-html="cardSummary.html" class="ql-editor"></div>
            <content-editor
              v-else
              v-model="cardSummary"
              ref="summaryEditor"
              :key="'summary-' + card.id"
              :placeholder="'Card summary...'"
              :disabled="dragging"
              highjackUndo
              :emitText="true"
              :hijackCopy="dragSelected"
              :hijackPaste="false"
              :allowFormats="false"
              :allowMarkdown="false"
              allowTraversal
              :allowEnterMoveText="false"
              @keyEnter="$emit('addNewLine', { order: card.order + 1, noteType: 'support' })"
              @keyDownLastLine="$emit('traverseDown', { direction: 'down', event: $event })"
              @keyUpFirstLine="traverse({ toRef: 'titleEditor', direction: 'up', event: $event })"
              @undo="$emit('undo')"
              @redo="$emit('redo')"
              @focus="focused = true"
              @blur="focused = false"
              @paste="log('paste this')"
            ></content-editor>
          </v-col>
          <v-btn
            v-show="hovering || !hidingChildren"
            icon
            :class="{ shrink: true, 'elevation-2': hidingChildren }"
            :ripple="false"
            v-if="viewMode == 'full'"
            @click="hidingChildren ? $emit('showChildren') : $emit('hideChildren')"
            style="transition: 0.4s ease all;  position: absolute; bottom: -31px; left: 50%; margin-left: -16px; background: white;"
          >
            <!-- <template v-if="hidingChildren">
              Show Notes
            </template>
            <template v-else>
              Hide Notes
            </template> -->
            <v-icon :style="!hidingChildren ? 'transform: rotate(180deg)' : null">
              keyboard_arrow_down
            </v-icon>
          </v-btn>
        </v-row>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import ContentEditor from '@/components/ContentEditor';
import WorkspaceUserSelect from '@/components/WorkspaceUserSelect';

import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { setTimeout } from 'timers';

import noteStatusesMixin from '../../mixins/noteStatusesMixin';

export default {
  mixins: [noteStatusesMixin],
  data() {
    return {
      editorReady: true,
      dragSelectEvent: false,
      focused: false
    };
  },
  props: ['card', 'dragSelected', 'dragSelecting', 'hidingChildren', 'viewMode', 'hovering'],
  components: {
    ContentEditor,
    WorkspaceUserSelect
  },
  computed: {
    ...mapState('views/context', ['selectedPageId', 'animating']),
    ...mapGetters('context', ['userCanEdit']),
    ...mapState('components/notepad', ['clipboard']),
    ...mapGetters('components/dragdrop', ['dragging']),
    ...mapState('components/dragdrop', ['draggedData']),
    ...mapGetters('workspaces', ['currentWorkspace']),
    cardTitle: {
      get() {
        return {
          html: this.card.title,
          updatedOn: this.card.updatedOn
        };
      },
      set(v) {
        this.updateCardTitle({
          pageId: this.selectedPageId,
          cardId: this.card.id,
          title: v
        });
      }
    },
    cardSummary: {
      get() {
        return { html: this.card.summary };
      },
      set(v) {
        this.setCardSummary({
          pageId: this.selectedPageId,
          cardId: this.card.id,
          summary: v
        });
      }
    },
    noteState: {
      get() {
        const state = this.card.state || 0;
        return this.noteStatuses[state];
      },
      set(v) {
        this.setCardState({
          pageId: this.selectedPageId,
          cardId: this.card.id,
          newState: v
        });
      }
    },
    // needsAttention: {
    //   get() {
    //     return !this.note.content.needsAttention;
    //   },
    //   set(v) {
    //     this.setNoteNeedsAttention({
    //       pageId: this.selectedPageId,
    //       noteBlockId: this.note.id,
    //       needsAttention: v
    //     });
    //   }
    // },
    assignedUser: {
      get() {
        const userIds = this.card.assignedTo;
        if (userIds && this.currentWorkspace.members) {
          return userIds.map(u => this.currentWorkspace.members[u]);
        } else {
          return [];
        }
      },
      set(v) {
        const ids = v.map(u => u.id);
        this.assignCardTo({ pageId: this.selectedPageId, cardId: this.card.id, userIds: ids });
      }
    },
    isDraggingSelf() {
      return this.draggedData.ids.includes(this.card.id);
    },
    isEmpty() {
      if (this.noteHtml.html) {
        const template = document.createElement('template');
        const html = this.noteHtml.html.trim(); // Never return a text node of whitespace as the result
        template.innerHTML = html;
        console.log(template);
        return template.content.firstChild.innerText.length == 0;
      } else {
        return true;
      }
    },
    workspaceMembers() {
      try {
        const members = Object.values(this.currentWorkspace.members);
        // const collabs = Object.values(members).filter(m => {
        //   return !this.permissionIds.includes(m.id) && m.id != this.userId;
        // });
        return members;
      } catch (e) {
        return [];
      }
    }
  },
  methods: {
    ...mapActions('pages', [
      'addNoteBlock',
      'updateCardTitle',
      'setCardState',
      'setNoteTask',
      'clearNoteTask',
      'assignCardTo',
      'setCardSummary'
    ]),
    ...mapMutations('views/context', ['setShowResourcePanel', 'setResourcePanelView']),
    ...mapMutations('components/notepad', ['setClipboard']),
    log(x) {
      console.log(x);
    },
    openResource() {
      this.setResourcePanelView(this.widgetResource.id);
      this.setShowResourcePanel(true);
    },
    handlePaste(event) {
      const pasteText = event.clipboardData.getData('text');
      console.log(pasteText);
      //  using contents and parent here for future use when copying resources
      const { contents, text, parent } = this.clipboard;
      console.log(pasteText);
      console.log(text);
      try {
        const pasteTextFirst = pasteText.replace(/\r?\n|\r/g, '~=');
        const textFirst = text.replace(/\r?\n|\r/g, '~=');
        console.log(pasteTextFirst, textFirst);
        if (pasteTextFirst == textFirst) {
          event.preventDefault();
          //  we are pasting our copied blocks
          console.log('paste note blocks');

          contents.forEach((n, i) => {
            this.addNoteBlock({
              html: n.html,
              order: this.note.order + i + 1,
              pageId: this.selectedPageId
            });
          });
        } else {
          //  we're pasting soething else. clear our coppiedBlocks
          if (this.clipboard.text != null) {
            this.setClipboard({ text: null, blockIds: [] });
          }
          return true;
        }
      } catch (e) {
        return true;
      }
    },
    handleMouseLeave() {
      if (this.hovering) {
        this.hovering = false;
      }
    },
    testDragSelectStart(e) {
      console.log('start');
      if (this.userCanEdit) {
        this.dragSelectEvent = e;
      }
    },
    stopDragSelect() {
      if (this.dragSelectEvent) {
        //  stop drag
        this.dragSelectEvent = false;
        window.removeEventListener('mouseup', this.stopDragSelect);
      }
    },
    async addNewLine(delta) {
      const newIndex = this.note.order + 1;
      //  update order of all higher up notes
      //  new vuex note here, returns id
      const newId = await this.addNoteBlock({
        html: '',
        order: newIndex,
        pageId: this.selectedPageId
      });

      this.$nextTick(() => {
        if (delta) {
          this.$emit('setNoteContent', { noteId: newId, delta: delta });
          //this.$refs['note' + newId][0].setContents(delta);
        }
        this.$emit('focusNoteBlock', { noteId: newId });
        //  this.$refs['note' + newId][0].focus();
      });
    },
    getContents() {
      return this.$refs.editor.editor.getContents();
    },
    traverse({ toRef, direction = 'up', event }) {
      try {
        const bound = event && event.bounds ? event.bounds.left : 0;
        const targetRef = this.$refs[toRef]; // either title or summary
        console.log(targetRef);
        targetRef.setCaret({
          prevLeft: bound,
          direction
        });
      } catch (e) {
        console.log(e);
      }
    },
    focusSummary({ bound, direction }) {
      this.$refs.summaryEditor.setCaret({
        prevLeft: bound,
        direction
      });
    },
    focusTitle({ bound, event, direction = 'up' }) {
      const newBound = bound ? bound : event && event.bounds ? event.bounds.left : 0;
      this.$refs.titleEditor.setCaret({
        prevLeft: newBound,
        direction
      });
    },
    focus() {
      this.$refs.editor.focus();
    }
  }
};
</script>

<style>
.note-card .ql-editor {
  line-height: 1.2;
  padding-bottom: 3px;
  font-size: 1.3em;
  font-weight: 600;
}

.question-block {
  /* margin-top: 34px; */
}

.dragging .question-block.first-block {
  margin-top: 0px;
  padding-top: 0px !important;
}

/* .dragging .question-block {
  margin-top: 0px;
  padding-top: 33px !important;
} */

.note-card .card-summary .ql-editor {
  font-size: 1em;
  font-weight: 400;
}
</style>
